import { ApiError, apiRequest } from '../../src/services/api';
import { t } from '../../src/services/language';
import { goToProvider } from './providerRedirect';
import type { GoogleProfile } from '../../src/shared/types';
import type { CalendarConnectReturn } from '../../renderer/src/global';

/**
 * Web'de takvim hesapları (Google, Outlook) sunucuda bağlı: yenileme
 * anahtarları sunucuda şifreli duruyor, takvim de oradan okunuyor — otomatik
 * bot katılımı sekme kapalıyken de çalışsın diye (web/lib/calendar/).
 *
 * Bağlama akışı:
 *  1. `startCalendarConnect`: sunucu onay adresini ve kullanıcıya bağlı imzalı
 *     `state`'i verir; `state` bu sekmede saklanır, sayfa sağlayıcıya gider.
 *  2. Sağlayıcı `/baglanti/<sağlayıcı>?code&state` ile uygulamaya döner.
 *     `captureCalendarReturn` (açılışta) `state`'i sakladığıyla karşılaştırır ve
 *     kodu kullanıcının oturumuyla sunucuya iletir (`/api/v1/calendar/exchange`).
 * Böylece başkasının başlattığı akış ya da başkasının kodu bu hesaba bağlanamaz.
 */

export type CalendarProvider = 'google' | 'microsoft';

export interface ProviderStatus {
  /** Sunucuda bu sağlayıcının OAuth istemcisi tanımlı mı. */
  configured: boolean;
  connected: boolean;
  /** Yetki iptal edildi / süresi doldu — yeniden bağlanmak gerekiyor. */
  expired: boolean;
  profile?: GoogleProfile;
}

export interface CalendarAccountsStatus {
  google: ProviderStatus;
  microsoft: ProviderStatus;
}

const CACHE_MS = 30_000;
const EMPTY: ProviderStatus = { configured: false, connected: false, expired: false };
const PENDING_KEY = 'talknote-takvim-baglama';

let cached: { at: number; value: CalendarAccountsStatus } | null = null;
let pending: Promise<CalendarAccountsStatus> | null = null;

/** Dönüşteki kod sunucuya iletilirken durum sorulursa önce o bitsin. */
let exchange: Promise<void> | null = null;
let connectReturn: CalendarConnectReturn | null = null;

export async function calendarAccounts(force = false): Promise<CalendarAccountsStatus> {
  if (exchange) await exchange;
  if (!force && cached && Date.now() - cached.at < CACHE_MS) return cached.value;
  pending ??= apiRequest<CalendarAccountsStatus>('GET', '/api/v1/calendar/status')
    .then((value) => {
      const normalized = { google: { ...EMPTY, ...value.google }, microsoft: { ...EMPTY, ...value.microsoft } };
      cached = { at: Date.now(), value: normalized };
      return normalized;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

export function forgetCalendarAccounts(): void {
  cached = null;
}

interface PendingConnect {
  provider: CalendarProvider;
  state: string;
  returnTo: string;
}

/**
 * Bağlama: sayfa sağlayıcının onay ekranına gider. Web'de söz hiç çözülmez
 * (sayfa gidiyor); mobilde tarayıcı dönüşsüz kapanırsa hatayla biter.
 */
export async function startCalendarConnect(provider: CalendarProvider): Promise<never> {
  const { url, state } = await apiRequest<{ url: string; state: string }>('POST', '/api/v1/calendar/connect', {
    body: { provider },
  });
  const pendingConnect: PendingConnect = {
    provider,
    state,
    returnTo: `${window.location.pathname}${window.location.search}`,
  };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pendingConnect));
  await goToProvider(url);
  throw new Error(t('Takvim bağlantısı tamamlanmadı.', 'The calendar connection was not completed.'));
}

export async function disconnectCalendar(provider: CalendarProvider): Promise<void> {
  await apiRequest('POST', '/api/v1/calendar/disconnect', { body: { provider } });
  forgetCalendarAccounts();
}

function readPending(): PendingConnect | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    const value = raw ? (JSON.parse(raw) as Partial<PendingConnect>) : null;
    if (!value || (value.provider !== 'google' && value.provider !== 'microsoft') || typeof value.state !== 'string') return null;
    const returnTo = typeof value.returnTo === 'string' && /^\/(?!\/)/.test(value.returnTo) ? value.returnTo : '/';
    return { provider: value.provider, state: value.state, returnTo };
  } catch {
    return null;
  }
}

/**
 * Açılışta, arayüz çizilmeden önce: sağlayıcıdan dönüş sayfasıysa
 * (`/baglanti/<sağlayıcı>`) kodu sunucuya iletmeye başlar ve adres çubuğunu
 * bağlamanın başladığı sayfaya çevirir (kod geçmişte kalmasın).
 */
export function captureCalendarReturn(): void {
  const match = /^\/baglanti\/(google|microsoft)\/?$/.exec(window.location.pathname);
  if (!match) return;
  const provider = match[1] as CalendarProvider;
  const params = new URLSearchParams(window.location.search);
  const saved = readPending();
  window.history.replaceState(null, '', saved?.returnTo ?? '/');

  const state = params.get('state');
  const code = params.get('code');
  if (!saved || saved.provider !== provider || !state || saved.state !== state) {
    connectReturn = { provider, ok: false, error: 'gecersiz' };
    return;
  }
  if (params.get('error') || !code) {
    connectReturn = { provider, ok: false, error: params.get('error') === 'access_denied' ? 'izin' : 'hata' };
    return;
  }
  exchange = apiRequest('POST', '/api/v1/calendar/exchange', { body: { provider, code, state }, timeoutMs: 30_000 })
    .then(() => {
      connectReturn = { provider, ok: true };
    })
    .catch((err: unknown) => {
      const reason = err instanceof ApiError ? err.reason : undefined;
      connectReturn = { provider, ok: false, error: reason === 'no_calendar_scope' ? 'izin' : 'hata' };
    })
    .finally(() => {
      cached = null;
      exchange = null;
    });
}

/** Bağlamanın sonucu (bir kez döner) — kod hâlâ iletiliyorsa bitmesini bekler. */
export async function takeCalendarConnectReturn(): Promise<CalendarConnectReturn | null> {
  if (exchange) await exchange;
  const value = connectReturn;
  connectReturn = null;
  return value;
}
